import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  FiShield, FiRefreshCw, FiDatabase, FiActivity, FiZap, FiHeadphones, 
  FiCheck, FiChevronDown, FiArrowRight, FiSearch, 
  FiLayers, FiTool, FiAlertCircle, FiClock, FiCheckCircle, FiMonitor
} from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { pageFaqs } from '../data/faqs';

const TechnicalSupport = () => {
  const location = useLocation();

  useEffect(() => {
    const id = location.hash.replace('#', '');
    if (id === 'website-support' || id === 'server-management' || id === 'retainers') {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    if (!location.hash) window.scrollTo(0, 0);
    
    // Page SEO Metadata
    document.title = "Technical Support Services | ManagedSite.net";
    
    const description = "ManagedSite.net provides technical support for websites, DNS, SSL, hosting, server management, email delivery, prepaid support blocks and urgent issues — for businesses and agencies.";
    const title = "Technical Support Services | ManagedSite.net";

    const updateMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };

    updateMeta('meta[name="description"]', 'content', description);
    updateMeta('meta[property="og:title"]', 'content', title);
    updateMeta('meta[property="og:description"]', 'content', description);
    updateMeta('meta[property="twitter:title"]', 'content', title);
    updateMeta('meta[property="twitter:description"]', 'content', description);
  }, [location.hash]);

  const [openFaq, setOpenFaq] = useState(null);

  const whyMatters = [
    {
      title: "Broken Functionality",
      text: "Forms, checkout, logins, plugins and integrations can fail without a clear cause.",
      icon: FiTool
    },
    {
      title: "Domain and DNS Issues",
      text: "Incorrect DNS, expired domains or email authentication problems can take services offline.",
      icon: FiLayers
    },
    {
      title: "Update and Compatibility Conflicts",
      text: "Plugin, theme and platform updates can create unexpected breakage.",
      icon: FiRefreshCw
    },
    {
      title: "Urgent Client Pressure",
      text: "Agencies need reliable technical help without hiring a full internal support team.",
      icon: FiClock
    }
  ];

  const supportCovers = [
    {
      title: "Website Troubleshooting",
      description: "Diagnose and resolve WordPress and website issues affecting functionality, stability or usability.",
      items: ["Plugin conflicts", "Theme issues", "Form failures", "Login problems", "Broken page behavior"]
    },
    {
      title: "Domains, DNS and SSL",
      description: "Help with domain settings, DNS records, SSL certificates and related connectivity issues.",
      items: ["DNS changes", "Domain connection issues", "SSL setup and repair", "Redirect problems", "Nameserver changes"]
    },
    {
      title: "Hosting and Server Issues",
      description: "Investigate hosting-related problems that affect website availability or performance.",
      items: ["Hosting misconfiguration", "Downtime investigation", "PHP and environment issues", "Migration support coordination", "Staging and production problems"]
    },
    {
      title: "Email and Deliverability Support",
      description: "Troubleshoot website email, authentication and delivery problems.",
      items: ["Contact form delivery", "SPF, DKIM and DMARC review", "Transactional email issues", "Postmark setup support", "Password reset and notification failures"]
    },
    {
      title: "Integrations and WooCommerce",
      description: "Support for connected systems, ecommerce issues and operational website workflows.",
      items: ["Plugin integrations", "Checkout troubleshooting", "Membership platform issues", "API and webhook problems", "Third-party service conflicts"]
    },
    {
      title: "Maintenance Escalations",
      description: "Handle technical work that goes beyond routine scheduled maintenance.",
      items: ["Update-related breakage", "Security incident response coordination", "Performance troubleshooting", "Recovery assistance", "Custom technical requests"]
    }
  ];

  const steps = [
    { title: "Submit the Issue", text: "Tell us what is happening, where it is happening and how urgent it is. Include access details when available." },
    { title: "Triage and Estimate", text: "We review the issue, identify likely causes and confirm the support approach and time expectations." },
    { title: "Diagnose and Resolve", text: "We investigate, apply fixes, test key functionality and document what changed." },
    { title: "Report Back", text: "You receive a clear summary your team can use internally or share with the client when appropriate." }
  ];

  const commonRequests = [
    "Website down or intermittently unavailable", "SSL warnings", "Domain not connecting", 
    "Email not sending from forms", "Plugin update broke the site", "Checkout or payment issues", 
    "Redirect and DNS problems", "Staging-to-production issues", "Malware cleanup coordination", 
    "Performance slowdowns", "User access and login problems", "Hosting environment troubleshooting",
    "cPanel/WHM and CloudLinux issues", "LiteSpeed or server environment problems"
  ];

  const faqs = pageFaqs['/services/technical-support'];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative bg-[#FAF8F3] overflow-hidden pt-24 pb-24 lg:pt-40 lg:pb-36">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="lg:grid lg:grid-cols-12 lg:gap-20 items-center">
            <div className="lg:col-span-7 text-center lg:text-left mb-20 lg:mb-0">
              <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-8">Technical Support</div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-[#17211E] tracking-tight leading-[1.1] mb-10">
                Practical Help When Websites and Systems Need Attention
              </h1>
              <p className="text-xl sm:text-2xl text-[#17211E]/80 leading-relaxed mb-12 max-w-2xl mx-auto lg:mx-0 font-medium">
                Website and system troubleshooting, server management, or reserved monthly time. New work starts on the contact form. Clients get a phone number and support desk after onboarding.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/contact" className="home-button">
                  Request Support
                </Link>
                <a href="#retainers" className="home-button-secondary">
                  How the work is sold
                </a>
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="relative rounded-[2rem] bg-[#EAF1EC] border border-[#3B7C6D]/20 shadow-2xl p-10 sm:p-12">
                <div className="absolute top-0 left-0 w-full h-2 bg-[#174C3C]"></div>
                <div className="space-y-6">
                  {[
                    { icon: FiMonitor, label: "Website Issues", status: "In Queue" },
                    { icon: FiLayers, label: "DNS and Domains", status: "Monitored" },
                    { icon: FiCheckCircle, label: "SSL and Hosting", status: "Reviewed" },
                    { icon: FiActivity, label: "Email Delivery", status: "Checked" },
                    { icon: FiZap, label: "Integrations", status: "Supported" },
                    { icon: FiHeadphones, label: "Agency Priority", status: "Active" }
                  ].map((item, i) => (
                    <div key={i} className="bg-white p-5 rounded-2xl flex items-center justify-between border border-gray-50">
                      <div className="flex items-center gap-4">
                        <div className="bg-[#EAF1EC] p-3 rounded-xl text-[#174C3C]">
                          <SafeIcon icon={item.icon} className="w-6 h-6" />
                        </div>
                        <span className="font-extrabold text-[#17211E]">{item.label}</span>
                      </div>
                      <span className="text-sm font-bold text-[#3B7C6D] bg-[#EAF1EC] px-3 py-1 rounded-full">{item.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white border-b border-[#EAF1EC] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-3">
            {[
              {
                href: '#website-support',
                label: 'Website and systems',
                text: 'The site, DNS, SSL, email, or an integration is broken. One job or ongoing help.'
              },
              {
                href: '#server-management',
                label: 'Server management',
                text: 'cPanel/WHM, LiteSpeed, CloudLinux, Imunify360. The box, not the WordPress site.'
              },
              {
                href: '#retainers',
                label: 'Hourly, prepaid, or retainer',
                text: 'How the work is sold. Reserved time is quoted after a review.'
              }
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="border border-[#17211E]/15 bg-[#FAF8F3] p-8 transition-colors hover:border-[#174C3C]"
              >
                <div className="text-2xl font-extrabold text-[#17211E] mb-4">{item.label}</div>
                <p className="text-lg text-[#17211E]/70 leading-relaxed">{item.text}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Why Technical Support Matters */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">Technical Problems Rarely Stay Isolated</h2>
            <p className="text-xl text-[#17211E]/70 leading-relaxed font-medium">
              A website issue can involve hosting, DNS, SSL, plugins, email delivery, forms, checkout, or third-party services. Fast, experienced troubleshooting protects client relationships and keeps projects moving.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyMatters.map((item, i) => (
              <div key={i} className="bg-[#FAF8F3] p-10 rounded-3xl border border-[#EAF1EC] hover:shadow-md transition-all">
                <div className="text-[#174C3C] mb-8">
                  <SafeIcon icon={item.icon} className="w-10 h-10" />
                </div>
                <h3 className="text-2xl font-bold text-[#17211E] mb-4">{item.title}</h3>
                <p className="text-lg text-[#17211E]/75 leading-relaxed font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Technical Support Covers */}
      <section id="website-support" className="scroll-mt-28 py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">What Technical Support Covers</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {supportCovers.map((service, i) => (
              <div key={i} className="bg-white p-10 md:p-12 rounded-[2rem] border border-[#EAF1EC] shadow-sm flex flex-col h-full">
                <h3 className="text-2xl font-extrabold text-[#17211E] mb-6">{service.title}</h3>
                <p className="text-xl text-[#17211E]/75 mb-10 leading-relaxed font-medium">{service.description}</p>
                <div className="flex-grow">
                  <div className="text-sm font-bold text-[#3B7C6D] uppercase tracking-wider mb-6">Included:</div>
                  <ul className="space-y-4">
                    {service.items.map((item, j) => (
                      <li key={j} className="flex items-start text-[#17211E]/80 text-lg font-medium">
                        <SafeIcon icon={FiCheck} className="w-5 h-5 text-[#174C3C] mr-4 mt-1 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="server-management" className="scroll-mt-28 py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#3B7C6D] mb-5">
              Also available
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-6 leading-tight">
              Server management
            </h2>
            <p className="text-xl text-[#17211E]/70 leading-relaxed font-medium mb-6">
              Separate from WordPress management. We manage and troubleshoot the server itself — cPanel/WHM, CloudLinux Manager, LiteSpeed, CloudLinux, NVMe, Imunify360, JetBackup, and KernelCare.
            </p>
            <p className="text-lg text-[#17211E]/60 font-medium mb-10">
              This is direct work with us. It is not white-label, and it is not an agency plan. Quoted on its own, as ongoing management or as a troubleshooting job.
            </p>
            <Link to="/get-started?start=server" className="home-button">
              Discuss Server Management
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {[
              { title: 'cPanel / WHM', text: 'Account and server administration, including CloudLinux Manager.' },
              { title: 'LiteSpeed', text: 'The web server that serves the sites.' },
              { title: 'CloudLinux', text: 'Account isolation and resource limits so one site cannot take down the others.' },
              { title: 'NVMe', text: 'Fast storage for websites and databases.' },
              { title: 'Imunify360', text: 'Server-level malware and intrusion protection.' },
              { title: 'JetBackup', text: 'Server backups and restores, separate from website-level backups.' },
              { title: 'KernelCare', text: 'Kernel updates without waiting on a reboot window.' }
            ].map((item) => (
              <div key={item.title} className="bg-[#FAF8F3] p-8 rounded-3xl border border-[#EAF1EC]">
                <h3 className="text-xl font-extrabold text-[#17211E] mb-3">{item.title}</h3>
                <p className="text-base text-[#17211E]/75 leading-relaxed font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="retainers" className="scroll-mt-28 py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#3B7C6D] mb-5">
              How the work is sold
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-6 leading-tight">
              Hourly, a prepaid block, or a retainer
            </h2>
            <p className="text-xl text-[#17211E]/70 leading-relaxed font-medium">
              Website care plans are separate. A retainer is reserved time each month so someone already knows the setup. It is quoted after a review.
            </p>
          </div>
          <div className="grid gap-8 lg:grid-cols-3">
            {[
              {
                title: 'Hourly',
                text: 'One job or an occasional fix. Billed in reasonable increments when there is no reserved time.'
              },
              {
                title: 'Prepaid block',
                text: 'A bank of hours, typically 5 or 10, valid for 12 months. Useful when the work is irregular.'
              },
              {
                title: 'Monthly retainer',
                text: 'Reserved support time each month for websites, email, hosting, or the server. Unused time does not roll over. Not a website care plan, and not white-label.'
              }
            ].map((item) => (
              <div key={item.title} className="bg-white p-10 border border-[#EAF1EC]">
                <h3 className="text-2xl font-extrabold text-[#17211E] mb-5">{item.title}</h3>
                <p className="text-lg text-[#17211E]/70 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-lg text-[#17211E]/60 font-medium max-w-3xl">
            Agency hourly time and prepaid blocks use partner rates. Direct retainers are quoted on their own. Emergency or after-hours help is not guaranteed.
          </p>
          <Link to="/get-started?start=support" className="home-link mt-8 inline-block text-lg">
            Discuss a support retainer
          </Link>
        </div>
      </section>

      {/* White-Label Support Model */}
      <section className="py-32 bg-[#174C3C] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div>
              <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-8">For Agencies</div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-10 leading-tight">Technical Support Delivered Behind Your Brand</h2>
              <p className="text-2xl text-[#FAF8F3]/80 mb-12 font-medium leading-relaxed">
                ManagedSite.net works as your technical team while your agency owns the client relationship. We can support your team directly or work behind the scenes, depending on the engagement.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-6 mb-16">
                {["White-label delivery", "Agency-first communication", "No direct solicitation of agency clients", "Clear handoff and documentation when needed", "Support for client websites under your account", "Optional coordination with your project managers"].map((item, i) => (
                  <div key={i} className="flex items-start text-xl font-bold">
                    <SafeIcon icon={FiCheck} className="w-6 h-6 text-[#D7B77A] mr-4 flex-shrink-0 mt-1" />
                    {item}
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/agency-services/white-label-support" className="home-button-on-dark">
                  White-label technical support
                </Link>
                <Link to="/contact?audience=agency" className="home-button-on-dark-secondary">
                  Request agency support
                </Link>
              </div>
            </div>
            <div className="hidden lg:block relative select-none opacity-50 text-center">
               <div className="bg-white/5 rounded-[3rem] p-16 border border-white/10 backdrop-blur-sm">
                  <div className="text-[#D7B77A] text-6xl font-black mb-10">SUPPORT</div>
                  <div className="h-1.5 w-24 bg-[#D7B77A] mx-auto mb-10"></div>
                  <div className="space-y-6">
                    <div className="h-4 bg-white/20 rounded-full w-full"></div>
                    <div className="h-4 bg-white/20 rounded-full w-5/6 mx-auto"></div>
                    <div className="h-4 bg-white/20 rounded-full w-4/6 mx-auto"></div>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#17211E] mb-6 leading-tight">Agency partner rates</h2>
          <p className="text-xl text-[#17211E]/70 leading-relaxed font-medium mb-8">
            Hourly time and prepaid blocks for agencies use partner rates. The same three ways to buy are above. Unused prepaid time does not convert to website care credits.
          </p>
          <Link to="/agency-services/white-label-support" className="home-link text-lg">
            See white-label technical support
          </Link>
        </div>
      </section>

      {/* Support vs Maintenance */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">Maintenance and Technical Support Are Different</h2>
            <p className="text-xl text-[#17211E]/75 leading-relaxed font-medium">
              Managed website plans include routine scheduled maintenance according to the selected plan. Technical support covers troubleshooting, changes, investigations and project-style work.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-16">
            <div className="bg-white p-12 rounded-[2.5rem] border border-[#EAF1EC] shadow-sm">
              <h3 className="text-2xl font-extrabold text-[#174C3C] mb-8 border-b border-[#EAF1EC] pb-6">Routine Management</h3>
              <ul className="space-y-6">
                {["WordPress updates", "Security monitoring", "Backups", "Uptime monitoring", "SSL monitoring", "Domain and DNS monitoring", "Monthly reporting"].map((item, i) => (
                  <li key={i} className="flex items-center text-xl font-bold text-[#17211E]">
                    <SafeIcon icon={FiCheck} className="w-6 h-6 text-[#174C3C] mr-4 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-12 rounded-[2.5rem] border border-[#EAF1EC] shadow-sm">
              <h3 className="text-2xl font-extrabold text-[#3B7C6D] mb-8 border-b border-[#EAF1EC] pb-6">Technical Support</h3>
              <ul className="space-y-6">
                {["Issue diagnosis", "Break/fix repairs", "Content and configuration changes", "Integration troubleshooting", "WooCommerce issue work", "Performance investigations", "Development and recovery requests"].map((item, i) => (
                  <li key={i} className="flex items-center text-xl font-bold text-[#17211E]">
                    <div className="w-6 h-6 bg-[#EAF1EC] rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                      <div className="w-2 h-2 bg-[#174C3C] rounded-full"></div>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="text-center">
            <p className="text-[#17211E]/50 font-bold italic text-lg mb-10">
              Some managed website plans include limited technical support time. Unused included support time does not roll over.
            </p>
            <Link to="/services/website-management" className="text-[#174C3C] font-extrabold hover:text-[#3B7C6D] transition-colors text-xl inline-flex items-center gap-3">
              Learn about Website Management <SafeIcon icon={FiArrowRight} />
            </Link>
          </div>
        </div>
      </section>

      {/* How Support Works */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-20 text-center">How Technical Support Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {steps.map((step, i) => (
              <div key={i} className="relative">
                <div className="text-6xl font-black text-[#EAF1EC] mb-8">{i + 1}</div>
                <h3 className="text-2xl font-bold text-[#17211E] mb-5">{step.title}</h3>
                <p className="text-lg text-[#17211E]/75 leading-relaxed font-medium">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Requests We Handle */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">Common Requests We Handle</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {commonRequests.map((item, i) => (
              <div key={i} className="bg-white p-6 rounded-2xl border border-[#EAF1EC] font-bold text-[#17211E] text-lg flex items-center gap-4 shadow-sm">
                <div className="w-2 h-2 rounded-full bg-[#174C3C]"></div>
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Direct Business Routing Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#FAF8F3] rounded-[2.5rem] p-12 md:p-20 shadow-xl border border-[#EAF1EC] text-center max-w-5xl mx-auto">
            <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-8">For Businesses</div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#17211E] mb-8 leading-tight">Need Technical Support for Your Own Business?</h2>
            <p className="text-xl text-[#17211E]/75 mb-12 font-medium leading-relaxed max-w-3xl mx-auto">
              Tell us what is happening, and we will help work out the next step.
            </p>
            <Link to="/contact" className="home-button">
              Tell Us What You Need Help With
            </Link>
          </div>
        </div>
      </section>

      {/* Related Technical Services */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">Related Technical Services</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-16">
            {[
              { label: "Website Management", link: "/services/website-management" },
              { label: "Server Management", link: "/services/technical-support#server-management" },
              { label: "Agency Plans", link: "/agency-plans" },
              { label: "Business Email", link: "/services/business-email" },
              { label: "Managed Hosting", link: "/services/managed-hosting" },
              { label: "Technology Consulting", link: "/services/technology-consulting" }
            ].map((item, i) => (
              <Link key={i} to={item.link} className="bg-white p-8 rounded-2xl border border-[#EAF1EC] font-bold text-[#17211E] text-center hover:shadow-md transition-all">
                {item.label}
              </Link>
            ))}
          </div>
          <div className="text-center">
            <Link to="/services" className="home-button-secondary">
              Explore All Services
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-24 bg-white border-t border-[#EAF1EC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#17211E] mb-16 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-[#FAF8F3] border border-[#EAF1EC] rounded-2xl overflow-hidden shadow-sm">
                <button 
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                  aria-controls={`faq-answer-${i}`}
                  className="w-full flex justify-between items-center p-8 text-left hover:bg-white transition-colors"
                >
                  <span className="text-xl font-bold text-[#17211E]">{faq.q}</span>
                  <SafeIcon icon={FiChevronDown} className={`w-6 h-6 text-[#3B7C6D] transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                <div 
                  id={`faq-answer-${i}`}
                  className={`px-8 pb-8 text-xl text-[#17211E]/75 font-medium leading-relaxed ${openFaq === i ? 'block' : 'hidden'}`}
                >
                  {faq.a}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-32 bg-[#174C3C] text-center relative overflow-hidden">
        <div className="absolute top-0 left-0 w-64 h-64 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full translate-x-1/3 translate-y-1/3"></div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-10 tracking-tight leading-tight">
            Get Dependable Technical Support Without Hiring Internally
          </h2>
          <p className="text-2xl text-[#FAF8F3]/80 mb-14 max-w-3xl mx-auto leading-relaxed">
            A reliable technical team for website issues, infrastructure problems, and urgent work — without hiring internally.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/digital-review" className="home-button-on-dark">
              Request Support
            </Link>
            <Link to="/agency-plans" className="home-button-on-dark-secondary">
              View Agency Plans
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TechnicalSupport;